import clsx from "clsx";
import { useContext, useEffect } from "react";
import { ToastContainer } from "react-toastify";

import { ClassroomContent } from "./classroom_content";
import { HeaderErrorBoundary, NullErrorBoundary, SidebarErrorBoundary } from "./error_boundary";
import { KeyboardShortcutsController } from "./keyboard_shortcuts_controller";
import { LocalModal } from "./local_modal";

import { Header, MobileHeader } from "components/header";
import { Observers } from "components/observers";
import { Sidebar } from "components/sidebar";
import { InstructorNotesWrapper } from "components/sidebar/timeline/instructor_notes_wrapper";
import { Tasks } from "components/tasks";
import { FlightAPIContext } from "contexts/flight_api_context";
import { IPContext } from "contexts/ip_context";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsAlreadyPresent } from "hooks/use_is_already_present";
import { useIsMobile } from "hooks/use_is_mobile";
import { useUserProperties } from "hooks/use_user_properties";
import { join, leave } from "shared/reducers/classroom";
import { PageUnloadReasonType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { classroomSelectors } from "store";
import { setJoinTimestamp } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";

import "react-toastify/dist/ReactToastify.css";
import "./classroom.scss";

function Classroom({ userId }: { userId: string }) {
  const dispatch = useAppDispatch();
  const flightAPI = useContext(FlightAPIContext);
  const IPController = useContext(IPContext);
  const isAlreadyPresent = useIsAlreadyPresent();
  const isMobile = useIsMobile();
  const isPresentationView = useAppSelector(classroomSelectors.selectIsPresentationView());

  const user = useUserProperties(userId, ["id", "enable_live_captions"]);
  const userExists = !!user?.id;

  useEffect(() => {
    const leaveClass = () => {
      if (!userExists) return;
      if (window.pageUnloadReason === PageUnloadReasonType.KICKED_OUT_OF_CLASS_NEW_TAB) {
        window.flightLogger.log(
          `[Classroom] User ${userId} is being kicked out since they entered the class in another tab or window.`,
        );
        if (isPresentationView) {
          dispatch(leave({ userId, isPresentationView }));
        }
        dispatch(
          createMonitoringEvent({
            type: MonitoringEventTypeType.LEAVE_CLASS_NEW_TAB,
            userId,
            contextData: { isPresentationView },
          }),
        );
      } else if (
        window.pageUnloadReason !== PageUnloadReasonType.KICKED_OUT_OF_CLASS_SERVER_REASSIGNMENT
      ) {
        window.pageUnloadReason = window.pageUnloadReason || PageUnloadReasonType.UNKNOWN;
        window.flightLogger.log(`[Classroom] User ${userId} is leaving the classroom.`);
        dispatch(leave({ userId, isPresentationView }));
        dispatch(
          createMonitoringEvent({
            type: MonitoringEventTypeType.LEAVE_CLASS,
            userId,
            contextData: {
              reason: window.pageUnloadReason,
              isPresentationView,
            },
          }),
        );
      }
    };
    window.addEventListener("pagehide", leaveClass);
    return () => window.removeEventListener("pagehide", leaveClass);
  }, [dispatch, userId, isPresentationView, userExists]);

  useEffect(() => {
    if (!userExists) return;
    window.flightLogger.log(`[Classroom] User ${userId} is joining the classroom.`);
    dispatch(join({ userId, isPresentationView }));
    dispatch(setJoinTimestamp({ timestamp: flightAPI.getServerMilliseconds() }));
    const { userAgent } = window.navigator;
    const ipAddress = IPController.getIP();
    const enableLiveCaptions = user.enable_live_captions;
    if (isAlreadyPresent) {
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.ENTER_CLASS_NEW_TAB,
          userId,
          contextData: { isPresentationView },
        }),
      );
    } else {
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.ENTER_CLASS,
          userId,
          contextData: { userAgent, ipAddress, isPresentationView, enableLiveCaptions },
        }),
      );
    }
    // enableLiveCaptions is not a dependency because we do not want to create a new "enter-class" monitoring event
    // every time the user's enable_live_captions property changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId, isPresentationView, IPController, userExists]);

  if (!userExists) return null;

  return (
    <>
      <div
        className={clsx(
          "body text-black-tint-20 flex h-screen overflow-hidden",
          isMobile ? "bg-black-tint-20" : "bg-black",
        )}
      >
        <div className="flex-auto flex flex-column">
          {isPresentationView ? null : (
            <HeaderErrorBoundary text="The header cannot be displayed">
              {isMobile ? <MobileHeader /> : <Header />}
            </HeaderErrorBoundary>
          )}
          <ClassroomContent />
        </div>
        {!isPresentationView && !isMobile && (
          <SidebarErrorBoundary text="The sidebar cannot be displayed">
            <Sidebar />
          </SidebarErrorBoundary>
        )}
      </div>
      {!isPresentationView && !isMobile && (
        <NullErrorBoundary>
          <KeyboardShortcutsController userId={userId} />
        </NullErrorBoundary>
      )}
      <NullErrorBoundary>
        <InstructorNotesWrapper />
      </NullErrorBoundary>
      <LocalModal />
      <Observers />
      <Tasks />
      {!isPresentationView && <ToastContainer pauseOnFocusLoss={false} />}
    </>
  );
}

export { Classroom };
